// Performance utility functions
import { useEffect, useState, useMemo } from 'react';

/**
 * Debounce function to limit how often a function can be called
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Throttle function to limit how often a function can be called
 * @param {Function} func - Function to throttle
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, delay) => {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      func.apply(null, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, delay);
    }
  };
};

/**
 * Optimized resize hook that debounces resize events
 * @param {Function} callback - Function to call on resize
 * @param {number} delay - Debounce delay (default 100ms)
 */
export const useOptimizedResize = (callback, delay = 100) => {
  const debouncedCallback = debounce(callback, delay);
  
  useEffect(() => {
    window.addEventListener('resize', debouncedCallback);
    return () => window.removeEventListener('resize', debouncedCallback);
  }, [debouncedCallback]);
};

/**
 * Intersection Observer hook for lazy loading
 * @param {Object} options - Intersection Observer options
 * @returns {Array} [ref, inView] - Ref to attach and visibility state
 */
export const useIntersectionObserver = (options = {}) => {
  const [inView, setInView] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(([entry]) => {
      setInView(entry.isIntersecting);
    }, options);

    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, options]);

  return [setRef, inView];
};

/**
 * Optimized search hook with debouncing
 * @param {string} searchTerm - Search term
 * @param {Function} searchFunction - Function to call with search term
 * @param {number} delay - Debounce delay (default 300ms)
 */
export const useOptimizedSearch = (searchTerm, searchFunction, delay = 300) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useMemo(
    () => debounce(async (term) => {
      if (!term.trim()) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const searchResults = await searchFunction(term);
        setResults(searchResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, delay),
    [searchFunction, delay]
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  return { results, loading };
};

/**
 * Virtual scrolling hook for large lists
 * @param {Array} items - Array of items to virtualize
 * @param {number} itemHeight - Height of each item
 * @param {number} containerHeight - Height of the container
 * @returns {Object} Virtual scrolling data
 */
export const useVirtualScrolling = (items, itemHeight, containerHeight) => {
  const [scrollTop, setScrollTop] = useState(0);
  
  const startIndex = Math.floor(scrollTop / itemHeight);
  const endIndex = Math.min(
    startIndex + Math.ceil(containerHeight / itemHeight) + 1,
    items.length
  );
  
  const visibleItems = items.slice(startIndex, endIndex);
  const totalHeight = items.length * itemHeight;
  const offsetY = startIndex * itemHeight;

  return {
    visibleItems,
    totalHeight,
    offsetY,
    startIndex,
    endIndex,
    onScroll: (e) => setScrollTop(e.target.scrollTop)
  };
};

/**
 * Smart polling hook that pauses when page is not visible
 * @param {Function} callback - Function to call on interval
 * @param {number} interval - Polling interval in milliseconds
 * @param {Array} dependencies - Dependencies that trigger restart
 */
export const useSmartPolling = (callback, interval, dependencies = []) => {
  useEffect(() => {
    if (!callback || !interval) return;

    let intervalId;
    let isVisible = !document.hidden;

    const startPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(callback, interval);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
      if (isVisible) {
        // Call immediately when becoming visible, then start polling
        callback();
        startPolling();
      } else {
        stopPolling();
      }
    };

    // Start initial polling
    callback(); // Call immediately
    startPolling();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, dependencies);
};
